/**
 * Wholesale VAT Toggle
 * Handles switching between Inc. VAT and Ex. VAT pricing display
 * UK VAT rate: 20% (Ex. VAT = Inc. VAT / 1.2)
 */

(function() {
  'use strict';

  const VAT_RATE = 0.20; // 20% UK VAT
  const VAT_MULTIPLIER = 1 + VAT_RATE; // 1.2
  const STORAGE_KEY = 'wholesale_vat_preference';

  // Default to Ex. VAT as requested
  const DEFAULT_MODE = 'ex';

  /**
   * Get current VAT mode from localStorage
   * @returns {string} 'inc' or 'ex'
   */
  function getVatMode() {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_MODE;
  }

  /**
   * Set VAT mode in localStorage and update HTML attribute
   * @param {string} mode - 'inc' or 'ex'
   */
  function setVatMode(mode) {
    localStorage.setItem(STORAGE_KEY, mode);
    document.documentElement.setAttribute('data-vat-mode', mode);
  }

  /**
   * Toggle between Inc. and Ex. VAT modes
   * @returns {string} The new mode
   */
  function toggleVatMode() {
    const currentMode = getVatMode();
    const newMode = currentMode === 'inc' ? 'ex' : 'inc';
    setVatMode(newMode);
    return newMode;
  }

  /**
   * Calculate Ex. VAT price from Inc. VAT price
   * @param {number} incVatPrice - Price including VAT (in smallest currency unit)
   * @returns {number} Price excluding VAT
   */
  function calculateExVatPrice(incVatPrice) {
    return Math.round(incVatPrice / VAT_MULTIPLIER);
  }

  /**
   * Format price for display using Shopify's money format
   * @param {number} priceInCents - Price in smallest currency unit (pence)
   * @returns {string} Formatted price string
   */
  function formatMoney(priceInCents) {
    // Use Shopify's money format if available, otherwise basic format
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(priceInCents);
    }
    // Fallback: basic GBP format
    const pounds = (priceInCents / 100).toFixed(2);
    return '£' + pounds;
  }

  /**
   * Update all toggle button displays
   * @param {string} mode - 'inc' or 'ex'
   */
  function updateToggleButtons(mode) {
    // Update active state on Ex. VAT options
    document.querySelectorAll('.vat-toggle__option--ex').forEach(function(el) {
      if (mode === 'ex') {
        el.classList.add('vat-toggle__option--active');
      } else {
        el.classList.remove('vat-toggle__option--active');
      }
    });

    // Update active state on Inc. VAT options
    document.querySelectorAll('.vat-toggle__option--inc').forEach(function(el) {
      if (mode === 'inc') {
        el.classList.add('vat-toggle__option--active');
      } else {
        el.classList.remove('vat-toggle__option--active');
      }
    });

    // The highlight position is controlled by CSS using [data-vat-mode] attribute on <html>
    // which is already set by the early script in the header
  }

  /**
   * Update all price displays on the page
   * @param {string} mode - 'inc' or 'ex'
   */
  function updatePriceDisplays(mode) {
    // Update VAT labels - hide if cart is empty
    const vatLabels = document.querySelectorAll('[data-vat-label]');
    vatLabels.forEach(label => {
      // Check cart total from nearby element
      const cartTotal = document.querySelector('[data-cart-total-inc-vat]');
      const cartValue = cartTotal ? parseInt(cartTotal.getAttribute('data-cart-total-inc-vat'), 10) : 0;

      if (cartValue === 0) {
        label.classList.add('hidden');
      } else {
        label.classList.remove('hidden');
        label.textContent = mode === 'inc' ? 'Inc. VAT' : 'Ex. VAT';
      }
    });

    // Update wholesale product card prices
    const cardPrices = document.querySelectorAll('[data-wholesale-price]');
    cardPrices.forEach(priceElement => {
      const incVatPrice = parseInt(priceElement.dataset.wholesalePrice, 10);
      const isTaxable = priceElement.dataset.taxable === 'true';

      if (!isTaxable || mode === 'inc') {
        // Show Inc. VAT price (original price)
        priceElement.textContent = formatMoney(incVatPrice) + '/unit';
      } else {
        // Show Ex. VAT price
        const exVatPrice = calculateExVatPrice(incVatPrice);
        priceElement.textContent = formatMoney(exVatPrice) + '/unit';
      }
    });

    // Update wholesale product page - unit price
    const unitPrices = document.querySelectorAll('[data-wholesale-unit-price]');
    unitPrices.forEach(priceElement => {
      const incVatPrice = parseInt(priceElement.dataset.wholesaleUnitPrice, 10);
      const isTaxable = priceElement.dataset.taxable === 'true';

      if (!isTaxable || mode === 'inc') {
        priceElement.textContent = formatMoney(incVatPrice);
      } else {
        const exVatPrice = calculateExVatPrice(incVatPrice);
        priceElement.textContent = formatMoney(exVatPrice);
      }
    });

    // Update wholesale product page - pack/case price
    const packPrices = document.querySelectorAll('[data-wholesale-pack-price]');
    packPrices.forEach(priceElement => {
      const incVatPrice = parseInt(priceElement.dataset.wholesalePackPrice, 10);
      const isTaxable = priceElement.dataset.taxable === 'true';

      if (!isTaxable || mode === 'inc') {
        priceElement.textContent = formatMoney(incVatPrice);
      } else {
        const exVatPrice = calculateExVatPrice(incVatPrice);
        priceElement.textContent = formatMoney(exVatPrice);
      }
    });

    // Dispatch custom event for any other components that need to react
    document.dispatchEvent(new CustomEvent('wholesale:vat-mode-changed', {
      detail: { mode: mode }
    }));
  }

  /**
   * Initialize the VAT toggle functionality
   */
  function init() {
    // Get initial mode and apply
    const initialMode = getVatMode();
    // Ensure the HTML attribute is set (in case early script didn't run)
    document.documentElement.setAttribute('data-vat-mode', initialMode);
    updateToggleButtons(initialMode);
    updatePriceDisplays(initialMode);

    // Bind click handlers for desktop toggle
    const desktopToggle = document.getElementById('vat-toggle-desktop');
    if (desktopToggle) {
      desktopToggle.addEventListener('click', function(e) {
        e.preventDefault();
        const newMode = toggleVatMode();
        updateToggleButtons(newMode);
        updatePriceDisplays(newMode);
      });
    }

    // Bind click handlers for mobile toggle
    const mobileToggle = document.getElementById('vat-toggle-mobile');
    if (mobileToggle) {
      const mobileToggleButton = mobileToggle.querySelector('.vat-toggle__track');
      if (mobileToggleButton) {
        mobileToggleButton.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          const newMode = toggleVatMode();
          updateToggleButtons(newMode);
          updatePriceDisplays(newMode);
          return false;
        });
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run price updates when new content is loaded (e.g., AJAX pagination)
  document.addEventListener('wholesale:products-loaded', function() {
    const mode = getVatMode();
    updatePriceDisplays(mode);
  });

  // Expose functions globally for external use if needed
  window.WholesaleVatToggle = {
    getMode: getVatMode,
    setMode: function(mode) {
      setVatMode(mode);
      updateToggleButtons(mode);
      updatePriceDisplays(mode);
    },
    toggle: function() {
      const newMode = toggleVatMode();
      updateToggleButtons(newMode);
      updatePriceDisplays(newMode);
      return newMode;
    },
    refresh: function() {
      const mode = getVatMode();
      updatePriceDisplays(mode);
    }
  };
})();
